/**
 * What an agent's turn changed, to review and undo (docs/refactor/02-revisao-de-diff.md): each file
 * with its lines added and deleted, its diff a click away, and "Desfazer" for one file or the whole
 * turn — back to the checkpoint taken before it. Desktop only: the files are on this machine.
 */

import { useEffect, useState } from 'react'
import { ChevronDown, ChevronRight, FlaskConical, GitCommitHorizontal, Loader2, Undo2 } from 'lucide-react'
import { sendCommand } from '../lib/dockPlacement'
import { useI18n } from '../lib/i18n'
import { repoChanges, repoFileDiff, repoRestoreCheckpoint, repoRestoreFile, type FileChange } from '../lib/desktop'

/**
 * `shared`: another agent worked in the same folder during the turn — only this turn's files are shown, and undoing the
 * turn goes file by file, so the other agent's work stays.
 */
export function ChangesPanel({ folder, checkpoint, initial, shared = false }: { folder: string; checkpoint: string; initial: FileChange[]; shared?: boolean }) {
  const { t } = useI18n()
  const [changes, setChanges] = useState<FileChange[]>(initial)
  const [open, setOpen] = useState<string | null>(null)
  const [diff, setDiff] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // A new turn brings a new checkpoint: start over with its changes.
  useEffect(() => {
    setChanges(initial)
    setOpen(null)
    setDiff(null)
    setError(null)
  }, [checkpoint, initial])

  const refresh = async () => {
    const now = await repoChanges(folder, checkpoint)
    const own = new Set(initial.map((c) => c.path))
    setChanges(shared ? now.filter((c) => own.has(c.path)) : now)
  }

  const toggle = async (file: string) => {
    if (open === file) {
      setOpen(null)
      return
    }
    setOpen(file)
    setDiff(null)
    try {
      setDiff(await repoFileDiff(folder, checkpoint, file))
    } catch (caught) {
      setDiff(String(caught))
    }
  }

  const undo = async (file: string | null) => {
    setBusy(file ?? '*')
    setError(null)
    try {
      if (file) await repoRestoreFile(folder, checkpoint, file)
      else if (shared) for (const change of changes) await repoRestoreFile(folder, checkpoint, change.path)
      else await repoRestoreCheckpoint(folder, checkpoint)
      if (open === file || !file) setOpen(null)
      await refresh()
    } catch (caught) {
      setError(t('changes.undoFailed', { reason: String(caught) }))
    } finally {
      setBusy(null)
    }
  }

  if (changes.length === 0) return <p className="tiny muted" style={{ margin: 0 }}>{t('changes.none')}</p>

  const added = changes.reduce((n, c) => n + (c.added ?? 0), 0)
  const deleted = changes.reduce((n, c) => n + (c.deleted ?? 0), 0)

  return (
    <div className="changes">
      <div className="changes__head tiny">
        <span>{t('changes.summary', { files: changes.length })}</span>
        <span className="changes__plus">+{added}</span>
        <span className="changes__minus">−{deleted}</span>
        <span className="spacer" />
        {/* After reviewing: the catalog's /testar and /commit, in this conversation (docs/refactor/02). */}
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => sendCommand('/testar')} disabled={busy !== null} title={t('composer.cmd.testHint')}>
          <FlaskConical size={12} /> {t('changes.test')}
        </button>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => sendCommand('/commit')} disabled={busy !== null} title={t('composer.cmd.commitHint')}>
          <GitCommitHorizontal size={12} /> {t('changes.commit')}
        </button>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => void undo(null)} disabled={busy !== null} title={t('changes.undoAllHint')}>
          {busy === '*' ? <Loader2 size={12} className="spin" /> : <Undo2 size={12} />} {t('changes.undoAll')}
        </button>
      </div>
      {error && <p className="tiny prompt-dock__notice--bad" role="alert" style={{ margin: 0 }}>{error}</p>}
      <ul className="changes__list">
        {changes.map((change) => (
          <li key={change.path} className="changes__file">
            <div className="changes__row">
              <button type="button" className="changes__name" onClick={() => void toggle(change.path)} aria-expanded={open === change.path}>
                {open === change.path ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                <span className={`changes__status changes__status--${change.status}`} title={t(`changes.status.${change.status}`)}>{change.status}</span>
                <span className="mono tiny truncate">{change.path}</span>
              </button>
              {change.binary ? (
                <span className="tiny muted">{t('changes.binary')}</span>
              ) : (
                <span className="tiny changes__counts">
                  <span className="changes__plus">+{change.added ?? 0}</span> <span className="changes__minus">−{change.deleted ?? 0}</span>
                </span>
              )}
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => void undo(change.path)} disabled={busy !== null} title={t('changes.undoHint')}>
                {busy === change.path ? <Loader2 size={12} className="spin" /> : <Undo2 size={12} />} {t('changes.undo')}
              </button>
            </div>
            {open === change.path && (diff === null ? <Loader2 size={12} className="spin" /> : <Diff text={diff} />)}
          </li>
        ))}
      </ul>
    </div>
  )
}

/** A unified diff, coloured: what was added, what was taken out, and where. */
function Diff({ text }: { text: string }) {
  const lines = text.split('\n').filter((line) => !/^(diff --git|index |--- |\+\+\+ |new file mode|deleted file mode)/.test(line))
  return (
    <pre className="diff mono tiny">
      {lines.map((line, index) => (
        <div key={index} className={line.startsWith('@@') ? 'diff__hunk' : line.startsWith('+') ? 'diff__add' : line.startsWith('-') ? 'diff__del' : 'diff__ctx'}>
          {line || ' '}
        </div>
      ))}
    </pre>
  )
}
